import { useEffect, useState } from 'react';
import { Bot, FileDown, Send, ShieldCheck, Pencil, AlertCircle } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Spinner } from '@/shared/ui/Spinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { formatDateTime } from '@/lib/utils';
import {
  useReport,
  useUpdateReport,
  useApproveReport,
  useGeneratePdf,
  usePdfUrl,
} from './reports.api';
import { SendApprovalDialog } from './SendApprovalDialog';

export function ReportDetailDialog({
  reportId,
  onClose,
}: {
  reportId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useReport(reportId);
  const update = useUpdateReport();
  const approve = useApproveReport();
  const genPdf = useGeneratePdf();
  const pdfUrl = usePdfUrl();
  const push = useToast((s) => s.push);

  const [editMode, setEditMode] = useState(false);
  const [summary, setSummary] = useState('');
  const [workDescription, setWorkDescription] = useState('');
  const [sendOpen, setSendOpen] = useState(false);

  useEffect(() => {
    if (data) {
      setSummary(data.summary ?? '');
      setWorkDescription(data.workDescription ?? '');
    }
  }, [data]);

  const isDraft = data?.status === 'GENERATED';
  const canEdit = ['DRAFT', 'GENERATED', 'REVIEW_REQUIRED', 'READY_TO_SEND'].includes(
    data?.status ?? '',
  );
  const canApprove = data?.status === 'REVIEW_REQUIRED';
  const canSend = data?.status === 'READY_TO_SEND';
  const canPdf = data && !['DRAFT', 'GENERATED'].includes(data.status);
  const missing = data?.missingInfoJson?.missing ?? [];

  const onSave = async () => {
    if (!reportId) return;
    try {
      await update.mutateAsync({ id: reportId, input: { summary, workDescription } });
      push('Bericht gespeichert.');
      setEditMode(false);
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Speichern fehlgeschlagen.', 'error');
    }
  };

  const onApprove = async () => {
    if (!reportId) return;
    try {
      await approve.mutateAsync({ id: reportId });
      push('Bericht intern freigegeben.');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Freigabe fehlgeschlagen.', 'error');
    }
  };

  const onPdf = async () => {
    if (!reportId || !data) return;
    try {
      const res = data.status === 'CUSTOMER_CONFIRMED' || data.status === 'CLOSED'
        ? await genPdf.mutateAsync(reportId)
        : await (async () => {
            try {
              return await pdfUrl.mutateAsync(reportId);
            } catch {
              return await genPdf.mutateAsync(reportId);
            }
          })();
      window.open(res.url, '_blank');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'PDF nicht verfügbar.', 'error');
    }
  };

  return (
    <>
      <Dialog open={!!reportId && !sendOpen} onClose={onClose} title={`Bericht v${data?.version ?? ''}`}>
        {isLoading || !data ? (
          <Spinner />
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={data.status} />
              <span className="text-xs text-slate-500">{formatDateTime(data.createdAt)}</span>
              {data.generatedBy && (
                <span className="text-xs text-slate-400">erzeugt: {data.generatedBy}</span>
              )}
              {data.approvedBy && (
                <span className="text-xs text-green-600">
                  freigegeben von {data.approvedBy.displayName}
                </span>
              )}
            </div>

            <div className="text-sm">
              <p className="font-medium text-slate-900">{data.project.title}</p>
              {data.project.reference && <p className="text-slate-500">{data.project.reference}</p>}
            </div>

            {isDraft && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <Bot className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <strong>KI-Entwurf – noch nicht geprüft.</strong> Bitte Inhalt kontrollieren und
                  bearbeiten. Erst danach ist die Freigabe möglich.
                </div>
              </div>
            )}

            {missing.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="mb-1 flex items-center gap-1.5 text-sm font-semibold text-red-700">
                  <AlertCircle className="h-4 w-4" /> Fehlende Angaben
                </p>
                <ul className="list-inside list-disc text-sm text-red-700">
                  {missing.map((m, i) => (
                    <li key={i}>{m}</li>
                  ))}
                </ul>
              </div>
            )}

            {editMode ? (
              <>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-slate-700">Zusammenfassung</label>
                  <textarea
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-slate-700">Ausgeführte Arbeiten</label>
                  <textarea
                    value={workDescription}
                    onChange={(e) => setWorkDescription(e.target.value)}
                    rows={5}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={onSave} loading={update.isPending}>
                    Speichern
                  </Button>
                  <Button variant="secondary" onClick={() => setEditMode(false)}>
                    Abbrechen
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <h3 className="mb-1 text-sm font-semibold text-slate-700">Zusammenfassung</h3>
                  <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-800">
                    {data.summary || '–'}
                  </p>
                </div>
                <div>
                  <h3 className="mb-1 text-sm font-semibold text-slate-700">Ausgeführte Arbeiten</h3>
                  <p className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-800">
                    {data.workDescription || '–'}
                  </p>
                </div>
              </>
            )}

            {(data.items?.length ?? 0) > 0 && !editMode && (
              <div>
                <h3 className="mb-1 text-sm font-semibold text-slate-700">
                  Positionen ({data.items!.length})
                </h3>
                <ul className="space-y-1 text-sm">
                  {data.items!.map((it) => (
                    <li key={it.id} className="flex justify-between rounded bg-slate-50 px-3 py-1.5">
                      <span className="text-slate-800">
                        <span className="mr-2 text-xs uppercase text-slate-400">{it.kind}</span>
                        {it.title}
                      </span>
                      {it.quantity && (
                        <span className="text-slate-600">
                          {Number(it.quantity).toLocaleString('de-DE')} {it.unit ?? ''}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!editMode && (
              <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4">
                {canEdit && (
                  <Button variant="secondary" onClick={() => setEditMode(true)}>
                    <Pencil className="h-4 w-4" /> Bearbeiten
                  </Button>
                )}
                {canApprove && (
                  <Button onClick={onApprove} loading={approve.isPending}>
                    <ShieldCheck className="h-4 w-4" /> Intern freigeben
                  </Button>
                )}
                {canSend && (
                  <Button onClick={() => setSendOpen(true)}>
                    <Send className="h-4 w-4" /> An Kunde senden
                  </Button>
                )}
                {canPdf && (
                  <Button variant="secondary" onClick={onPdf} loading={genPdf.isPending || pdfUrl.isPending}>
                    <FileDown className="h-4 w-4" /> PDF
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Dialog>
      {data && (
        <SendApprovalDialog
          open={sendOpen}
          onClose={() => setSendOpen(false)}
          report={data}
          onSent={() => {
            setSendOpen(false);
            onClose();
          }}
        />
      )}
    </>
  );
}
